'use client';

import { useState, useEffect } from 'react';
import Sidebar from '@/components/common/Sidebar';
import ConnectionStatus from '@/components/common/ConnectionStatus';
import PeriodSelector from '@/components/common/PeriodSelector';
import EntitySelector from '@/components/common/EntitySelector';
import AlertBadge from '@/components/common/AlertBadge';
import KPICard from './KPICard';
import DigitalTwinChart from './DigitalTwinChart';
import ActivityPulseSankey from './ActivityPulseSankey';
import TransactionStream from './TransactionStream';

interface KPIData {
  title: string;
  value: string | number;
  trend: number;
  status: 'success' | 'warning' | 'error';
  icon: string;
  sparklineData: number[];
}

interface BalanceData {
  category: string;
  beginning: number;
  activity: number;
  ending: number;
  variance: number;
}

interface FlowData {
  source: string;
  target: string;
  value: number;
  color: string;
}

interface Transaction {
  id: string;
  timestamp: string;
  account: string;
  description: string;
  debit: number;
  credit: number;
  hash: string;
  status: 'verified' | 'pending' | 'flagged';
}

const LiveMirrorInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null) {
      setSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  const kpiData: KPIData[] = [
    {
      title: 'Balance Sheet Integrity',
      value: '99.8%',
      trend: 0.2,
      status: 'success',
      icon: 'ShieldCheckIcon',
      sparklineData: [98.5, 98.8, 99.1, 99.3, 99.5, 99.6, 99.7, 99.8],
    },
    {
      title: 'Active Transactions',
      value: '12,847',
      trend: 15.3,
      status: 'success',
      icon: 'BoltIcon',
      sparklineData: [9200, 9800, 10400, 11000, 11600, 12000, 12400, 12847],
    },
    {
      title: 'Data Drift Alerts',
      value: '3',
      trend: -25.0,
      status: 'warning',
      icon: 'ExclamationTriangleIcon',
      sparklineData: [8, 7, 6, 5, 4, 4, 3, 3],
    },
    {
      title: 'Close Progress',
      value: '87%',
      trend: 12.0,
      status: 'success',
      icon: 'ChartBarIcon',
      sparklineData: [45, 52, 60, 68, 73, 78, 82, 87],
    },
  ];

  const balanceData: BalanceData[] = [
    {
      category: 'Cash & Equivalents',
      beginning: 45000000,
      activity: 2500000,
      ending: 47500000,
      variance: 0.8,
    },
    {
      category: 'Accounts Receivable',
      beginning: 32000000,
      activity: -1200000,
      ending: 30800000,
      variance: -2.1,
    },
    {
      category: 'Inventory',
      beginning: 28000000,
      activity: 3400000,
      ending: 31400000,
      variance: 1.5,
    },
    {
      category: 'Fixed Assets',
      beginning: 125000000,
      activity: -2800000,
      ending: 122200000,
      variance: -0.3,
    },
    {
      category: 'Accounts Payable',
      beginning: 18000000,
      activity: 1800000,
      ending: 19800000,
      variance: 1.2,
    },
    {
      category: 'Long-term Debt',
      beginning: 75000000,
      activity: -5000000,
      ending: 70000000,
      variance: -0.5,
    },
  ];

  const flowData: FlowData[] = [
    { source: 'Revenue', target: 'Cash', value: 15000000, color: '#10b981' },
    { source: 'Cash', target: 'Inventory', value: 3400000, color: '#3b82f6' },
    { source: 'Cash', target: 'Debt Payment', value: 5000000, color: '#f59e0b' },
    { source: 'AR Collection', target: 'Cash', value: 8200000, color: '#10b981' },
    { source: 'Inventory', target: 'COGS', value: 2800000, color: '#ef4444' },
  ];

  const transactions: Transaction[] = [
    {
      id: 'TXN-2026-02-07-001',
      timestamp: '2026-02-07T19:42:15.000Z',
      account: '1010-Cash',
      description: 'Wire transfer from customer ABC Corp',
      debit: 125000.0,
      credit: 0,
      hash: 'a7f3c9d2e8b1f4a6c3d9e2f8b1a4c7d3',
      status: 'verified',
    },
    {
      id: 'TXN-2026-02-07-002',
      timestamp: '2026-02-07T19:41:48.000Z',
      account: '5010-COGS',
      description: 'Inventory purchase from supplier XYZ',
      debit: 0,
      credit: 45000.0,
      hash: 'b2e8f1c4d7a3e9f2c8b1d4a7e3f9c2b8',
      status: 'verified',
    },
    {
      id: 'TXN-2026-02-07-003',
      timestamp: '2026-02-07T19:40:22.000Z',
      account: '2010-AP',
      description: 'Vendor payment processing',
      debit: 32000.0,
      credit: 0,
      hash: 'c9f2e8b1d4a7c3e9f2b8d1a4c7e3f9b2',
      status: 'pending',
    },
    {
      id: 'TXN-2026-02-07-004',
      timestamp: '2026-02-07T19:39:55.000Z',
      account: '4010-Revenue',
      description: 'Sales invoice INV-2026-0207',
      debit: 0,
      credit: 89000.0,
      hash: 'd4a7c3e9f2b8d1a4c7e3f9b2c8e1f4a7',
      status: 'verified',
    },
    {
      id: 'TXN-2026-02-07-005',
      timestamp: '2026-02-07T19:38:10.000Z',
      account: '1200-AR',
      description: 'Customer payment received',
      debit: 67000.0,
      credit: 0,
      hash: 'e3f9b2c8e1f4a7d3c9e2f8b1a4c7d3e9',
      status: 'flagged',
    },
    {
      id: 'TXN-2026-02-07-006',
      timestamp: '2026-02-07T19:37:45.000Z',
      account: '6010-Expenses',
      description: 'Office supplies purchase',
      debit: 0,
      credit: 2500.0,
      hash: 'f2b8d1a4c7e3f9b2c8e1f4a7d3c9e2f8',
      status: 'verified',
    },
  ];

  const handleDrillDown = (category: string) => {
    console.log('Drilling down into category:', category);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-mono text-sm text-text-secondary">Loading Live Mirror Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} />
      <div
        className={`transition-all duration-250 ease-out ${
          sidebarCollapsed ? 'md:ml-20' : 'md:ml-60'
        } pb-16 md:pb-0`}
      >
        <header className="sticky top-0 z-header bg-primary border-b border-border shadow-md">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="font-mono text-2xl font-semibold text-foreground">Live Mirror Dashboard</h1>
                <p className="font-sans text-sm text-text-secondary mt-1">
                  Real-time Financial Close Command Center
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <EntitySelector />
                <PeriodSelector />
                <ConnectionStatus />
                <AlertBadge />
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 py-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiData.map((kpi, index) => (
              <KPICard key={index} {...kpi} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DigitalTwinChart data={balanceData} onDrillDown={handleDrillDown} />
            </div>
            <div className="lg:col-span-1">
              <ActivityPulseSankey flows={flowData} />
            </div>
          </div>

          <TransactionStream transactions={transactions} />
        </main>
      </div>
    </div>
  );
};

export default LiveMirrorInteractive;